import React, { useEffect, useState } from "react";
import { UserPlus, Trash2, X, Loader2, Users as UsersIcon, Shield } from "lucide-react";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import { PageHeader, Loading, DataTable, Card, StatusBadge } from "../components/common";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const ROLES = ["Administrator", "AI Governance Officer", "Risk Officer", "Data Governance Officer", "Security Officer", "Model Owner", "Agent Owner", "Compliance Officer", "Regulatory Officer", "Approver", "Auditor", "Business Owner", "Developer", "Viewer"];

export default function Users() {
  const { t } = useI18n();
  const { user: me } = useAuth();
  const [users, setUsers] = useState(null);
  const [groups, setGroups] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "Sandbox@123", role: "Viewer", groups: [] });
  const [saving, setSaving] = useState(false);

  const load = () => { api.get("/users").then((r) => setUsers(r.data)); api.get("/groups").then((r) => setGroups(r.data)); };
  useEffect(load, []);
  if (!users) return <Loading />;

  const create = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await api.post("/users", form); toast.success("User created"); setShow(false); setForm({ name: "", email: "", password: "Sandbox@123", role: "Viewer", groups: [] }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  const del = async (u) => { if (!window.confirm(`Delete ${u.email}?`)) return; try { await api.delete(`/users/${u.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };

  const cols = [
    { key: "name", label: t("name"), render: (r) => <span className="font-medium">{r.name}</span> },
    { key: "email", label: t("email") },
    { key: "role", label: t("role"), render: (r) => <span className="rounded-md bg-slate-100 px-2 py-0.5 text-xs font-medium">{r.role}</span> },
    { key: "groups", label: "Groups", render: (r) => (r.groups || []).join(", ") },
    { key: "permissions", label: "Permissions", render: (r) => <span className="text-xs text-slate-400">{(r.permissions || []).length} perms</span> },
    { key: "actions", label: t("actions"), render: (r) => r.email !== "admin@itoo.qa" ? <button data-testid={`del-${r.id}`} onClick={(e) => { e.stopPropagation(); del(r); }} className="rounded-lg p-1.5 text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button> : <span className="text-xs text-slate-300">protected</span> },
  ];

  return (
    <div>
      <PageHeader title={t("nav.users")} subtitle={`${users.length} users · ${groups.length} groups · granular RBAC`} summary="User and group management. Only the administrator can add users, manage groups and configure permissions.">
        <button data-testid="add-user-btn" onClick={() => setShow(true)} className="qatar-gradient inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold text-white"><UserPlus className="h-4 w-4" /> Add User</button>
      </PageHeader>

      <DataTable testid="users-table" columns={cols} rows={users} pageSize={15} />

      <h3 className="mb-3 mt-8 text-lg font-semibold font-head">Groups</h3>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {groups.map((g) => (
          <Card key={g.id} title={g.name} className="!p-4">
            <p className="text-xs text-slate-500">{g.description}</p>
            <div className="mt-2 flex items-center gap-2 text-xs text-slate-400"><UsersIcon className="h-3.5 w-3.5" /> {g.members} members</div>
            <div className="mt-2 flex flex-wrap gap-1">{(g.permissions || []).map((p) => <span key={p} className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-600">{p}</span>)}</div>
          </Card>
        ))}
      </div>

      {show && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" onClick={() => setShow(false)}>
          <div data-testid="add-user-modal" className="w-full max-w-md rounded-2xl bg-white p-6 card-shadow-lg" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-center justify-between"><h3 className="text-lg font-bold font-head">Add User</h3><button onClick={() => setShow(false)} className="rounded-lg p-1.5 hover:bg-slate-100"><X className="h-5 w-5" /></button></div>
            <form onSubmit={create} className="space-y-3">
              <input data-testid="user-name" placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
              <input data-testid="user-email" type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
              <input data-testid="user-password" type="text" placeholder="Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
              <select data-testid="user-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm">{ROLES.map((r) => <option key={r}>{r}</option>)}</select>
              <select data-testid="user-group" value={form.groups[0] || ""} onChange={(e) => setForm({ ...form, groups: e.target.value ? [e.target.value] : [] })} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"><option value="">No group</option>{groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}</select>
              <button data-testid="user-create" type="submit" disabled={saving} className="qatar-gradient flex w-full items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-semibold text-white disabled:opacity-60">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Create User</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
